import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  BuildingOfficeIcon,
  ClockIcon,
  BellIcon,
  BanknotesIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

// Composants des sections
import GeneralSettings from '../../components/settings/GeneralSettings';
import BusinessHours from '../../components/settings/BusinessHours';
import NotificationSettings from '../../components/settings/NotificationSettings';
import BillingSettings from '../../components/settings/BillingSettings';
import MembershipSettings from '../../components/settings/MembershipSettings';
import SecuritySettings from '../../components/settings/SecuritySettings';

import { mockSettings } from '../../utils/mockData';

const SettingsPage = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);

  // Récupération des paramètres
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        const { data } = await axios.get('/settings');
        return data;
      } catch (err) {
        // Si l'utilisateur n'est pas authentifié ou qu'il y a une erreur, on tente la route publique
        try {
          const { data } = await axios.get('/settings/public');
          return data;
        } catch (innerErr) {
          console.warn("Utilisation des données de test en production (échec API)");
          return mockSettings; // Utiliser les données de test si l'API n'est pas disponible
        }
      }
    }
  });

  // Mutation pour sauvegarder les paramètres
  const saveMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const { data } = await axios.put('/settings', updatedSettings);
      return data;
    },
    onSuccess: () => {
      toast.success('Paramètres sauvegardés avec succès');
      setHasChanges(false);
      queryClient.invalidateQueries(['settings']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde');
    }
  });

  // Mutation pour réinitialiser les paramètres
  const resetMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/settings/reset');
      return data;
    },
    onSuccess: () => {
      toast.success('Paramètres réinitialisés avec succès');
      setHasChanges(false);
      queryClient.invalidateQueries(['settings']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || 'Erreur lors de la réinitialisation');
    }
  });

  const tabs = [
    {
      id: 'general',
      name: 'Général',
      icon: BuildingOfficeIcon,
      component: GeneralSettings
    },
    {
      id: 'hours',
      name: 'Horaires',
      icon: ClockIcon,
      component: BusinessHours
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: BellIcon,
      component: NotificationSettings
    },
    {
      id: 'billing',
      name: 'Facturation',
      icon: BanknotesIcon,
      component: BillingSettings
    },
    {
      id: 'membership',
      name: 'Adhésions',
      icon: UserGroupIcon,
      component: MembershipSettings
    },
    {
      id: 'security',
      name: 'Sécurité',
      icon: ShieldCheckIcon,
      component: SecuritySettings
    }
  ];

  const handleSave = () => {
    if (settings) {
      saveMutation.mutate(settings);
    }
  };

  const handleReset = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres ?')) {
      resetMutation.mutate();
    }
  };

  const handleSettingsChange = (newSettings) => {
    setHasChanges(true);
    queryClient.setQueryData(['settings'], newSettings);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 backdrop-blur-lg rounded-3xl p-8 border border-red-500/20 text-center">
        <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-red-300 mb-2">
          Erreur de chargement
        </h3>
        <p className="text-red-200">
          {error.message || 'Impossible de charger les paramètres'}
        </p>
      </div>
    );
  }

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {t('settings')}
          </h1>
          <p className="text-white/60 mt-2">
            Configuration et paramètres système
          </p>
        </div>
        
        <div className="flex gap-3">
          {hasChanges && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4" />
              {saveMutation.isLoading ? 'Sauvegarde...' : 'Sauvegarder'}
            </motion.button>
          )}
          
          <button
            onClick={handleReset}
            disabled={resetMutation.isLoading}
            className="flex items-center gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 px-4 py-2 rounded-xl transition-colors border border-red-500/20 disabled:opacity-50"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {resetMutation.isLoading ? 'Réinitialisation...' : 'Réinitialiser'}
          </button>
        </div>
      </div>

      {/* Notification des changements */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 backdrop-blur-lg rounded-2xl p-4 border border-amber-500/20"
        >
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
            <p className="text-amber-200">
              Vous avez des modifications non sauvegardées. N'oubliez pas de sauvegarder vos changements.
            </p>
          </div>
        </motion.div>
      )}

      {/* Onglets */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Contenu de l'onglet actif */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {ActiveComponent && settings && (
            <ActiveComponent 
              settings={settings} 
              onSettingsChange={handleSettingsChange}
              onHasChanges={setHasChanges}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SettingsPage; 